import React, { useState, useEffect, useRef } from 'react';
import { LISTING_STATUS } from './listings';
import PropertyCard from './PropertyCard.jsx';
import { StyleConstants } from './StyleConstants';
import { FaChevronDown } from 'react-icons/fa';
import './GalleryMobile.css'; // Styles for mobile gallery view


// Status filter options (match SplitView)
const STATUS_FILTERS = [
  { label: 'All Recent', value: 'all' },
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Closed', value: 'Closed' }
];

// Modern card grid layout styles
const styles = {
  container: {
    width: '100%', 
    maxWidth: '100%',
    margin: 0,
    padding: 0, // No padding here since it's handled by the parent container
    backgroundColor: 'transparent', // Changed to transparent to let parent background show through
    boxSizing: 'border-box',
  },
  // Note: We're now using the gallery-view-grid CSS class instead of inline styles
  // The styles are defined in src/styles/gallery-view.css
  gridContainer: {
    marginTop: '8px',
    marginBottom: '32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px', /* Matches SplitView's spacing */
    marginTop: 0,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  filterSelect: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px 8px 12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    background: '#fff',
    color: '#111',
    fontWeight: 600,
    fontSize: '16px',
    cursor: 'pointer',
    height: '40px',
    minWidth: '140px',
    textAlign: 'left'
  },
  sectionTitle: {
    ...StyleConstants.sectionTitle,
    margin: 0,
    lineHeight: '1.15', /* Exactly matching SplitView's lineHeight */
    padding: 0
  },
  // Do not use this - use StyleConstants.statusCounter directly
  statusCounter: StyleConstants.statusCounter,
  noListings: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: 'var(--grey)',
    color: '#666',
    borderRadius: '8px',
    fontSize: '16px',
  }
};

function groupClosedByYear(listings) {
  // Returns { [year]: [listing, ...] }
  return listings.reduce((acc, listing) => {
    if (listing.status === LISTING_STATUS.CLOSED) {
      const year = new Date(listing.closeDate).getFullYear();
      acc[year] = acc[year] || [];
      acc[year].push(listing);
    }
    return acc;
  }, {});
}

// No longer needed - we're using cards instead of a table

export default function ListingList({ listings, filterStatus = 'all', onFilterChange, isLoading, isMobile }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // DEBUG: Log listings received by ListingList
  // Removed debug log for production
  
  // Sort by price (highest to lowest) within each status group
  const sortByPrice = (listings) => {
    return [...listings].sort((a, b) => {
      const priceA = parseFloat(a.price) || 0;
      const priceB = parseFloat(b.price) || 0;
      return priceB - priceA;
    });
  };

  // Separate by status and sort by price
  const active = sortByPrice(listings.filter(l => l.status === LISTING_STATUS.ACTIVE));
  const pending = sortByPrice(listings.filter(l => l.status === LISTING_STATUS.PENDING));
  const closed = sortByPrice(listings.filter(l => l.status === LISTING_STATUS.CLOSED));

  React.useEffect(() => {
    if (listings && listings.length) {
      const withPhotos = listings.filter(l => l.photo).length;
    }
  }, [listings, active.length, pending.length, closed.length]);
  
  // Group closed by year and sort each year by price (not used in current view but keeping the function for future use)
  const closedByYear = groupClosedByYear(closed);
  Object.keys(closedByYear).forEach(year => {
    closedByYear[year] = sortByPrice(closedByYear[year]);
  });

  // Filter if specific status is requested
  let toShow = [];
  if (filterStatus === LISTING_STATUS.ACTIVE) toShow = active;
  else if (filterStatus === LISTING_STATUS.PENDING) toShow = pending;
  else if (filterStatus === LISTING_STATUS.CLOSED) toShow = closed;
  else toShow = [...active, ...pending, ...closed];

  // Calculate total listings count based on current filter
  const getListingCount = () => {
    if (filterStatus === 'all') return listings.length;
    if (filterStatus === LISTING_STATUS.ACTIVE) return active.length;
    if (filterStatus === LISTING_STATUS.PENDING) return pending.length;
    if (filterStatus === LISTING_STATUS.CLOSED) return closed.length;
    if (filterStatus === 'Active') return active.length;
    if (filterStatus === 'Pending') return pending.length;
    if (filterStatus === 'Closed') return closed.length;
    return 0;
  };

  // Get the display name for the current filter

  // Render the listings in a grid
  const renderListings = () => {
    if (isLoading) {
      return (
        <div style={{ ...styles.noListings, color: '#333' }}> {/* Using similar style for consistency */}
          Loading listings...
        </div>
      );
    }

    if (toShow.length === 0) {
      return (
        <div style={styles.noListings}>
          No listings found.
        </div>
      );
    }

    return (
      <div 
      className="gallery-view-grid" 
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', // From c889d6f StyleConstants
        width: '100%', // From c889d6f StyleConstants
        gap: '32px', // From c889d6f ListingList.jsx (override)
        marginTop: '8px', // From c889d6f ListingList.jsx
        marginBottom: '32px' // From c889d6f ListingList.jsx
        // NO justifyItems, NO padding, NO boxSizing as per c889d6f effective style
      }}
    >
        {toShow.map((listing) => (
          <PropertyCard key={listing.id} listing={listing} isMobile={isMobile} />
        ))}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header with filter dropdown - exactly matching SplitView structure */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 style={{
            ...StyleConstants.sectionTitle,
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
            padding: 0,
            lineHeight: '32px', /* Use exact px value from StyleConstants rather than relative value */
            fontSize: '24px', /* Explicitly set font size to match SplitView */
            fontWeight: '700' /* Explicitly set font weight to match SplitView */
          }}>
            My Listings
            <span style={StyleConstants.statusCounter}>
              ({getListingCount()})
            </span>
          </h2>
        </div>
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={styles.filterSelect}
          >
            <span>{STATUS_FILTERS.find(opt => opt.value === filterStatus)?.label || 'All Recent'}</span>
            <FaChevronDown style={{ marginLeft: '12px', fontSize: '14px', color: '#666' }} />
          </button>
          
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '8px',
              marginTop: '4px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              overflow: 'hidden'
            }}>
              {STATUS_FILTERS.map(({ label, value }) => (
                <div
                  key={value}
                  onClick={() => {
                    onFilterChange({ target: { value } });
                    setShowDropdown(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    backgroundColor: value === filterStatus ? '#f5f5f5' : 'transparent',
                    ':hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Show listings in a single grid */}
      {renderListings()}
    </div>
  );
}

// ListingCard component replaced by PropertyCard
